import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, RequestFailed } from '../api/client';
import { useApp } from '../state/AppState';
import { PasswordField } from './PasswordField';

export function JoinScreen() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { register, session } = useApp();

  const [checking, setChecking] = useState(true);
  const [invalidReason, setInvalidReason] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ошибку показываем не на каждое нажатие клавиши, а когда второе поле
  // уже заполнено настолько же — иначе она мигает во время набора
  const mismatch = passwordAgain.length >= password.length && passwordAgain !== password
    && passwordAgain.length > 0;

  // Проверяем код до показа формы: заполнять её впустую — обидно
  useEffect(() => {
    let cancelled = false;
    api.checkInvite(code)
      .then((result) => {
        if (cancelled) return;
        setInvalidReason(result.valid ? null : result.reason ?? 'Приглашение недействительно');
      })
      .catch(() => !cancelled && setInvalidReason('Не удалось проверить приглашение'))
      .finally(() => !cancelled && setChecking(false));
    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== passwordAgain) {
      setError('Пароли не совпадают — проверьте оба поля');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await register({
        inviteCode: code,
        username: username.trim().toLowerCase(),
        displayName: displayName.trim(),
        password,
      });
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось соединиться с сервером');
    } finally {
      setBusy(false);
    }
  }

  if (checking) {
    return <div className="centered"><p className="sub">Проверяем приглашение…</p></div>;
  }

  if (invalidReason) {
    return (
      <div className="centered">
        <div className="card">
          <h1>Приглашение не подходит</h1>
          <p className="sub">{invalidReason}</p>
          <button onClick={() => navigate('/', { replace: true })}>На страницу входа</button>
        </div>
      </div>
    );
  }

  return (
    <div className="centered">
      <form className="card" onSubmit={submit}>
        <h1>Добро пожаловать</h1>
        <p className="sub">Вас пригласили. Осталось придумать логин и пароль.</p>

        <div className="field">
          <label htmlFor="displayName">Как вас показывать</label>
          <input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Например, Мария"
            maxLength={64}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="username">Логин для входа</label>
          <input
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="латиница, цифры и _"
            autoCapitalize="none"
            spellCheck={false}
            pattern="[a-zA-Z0-9_]{3,32}"
            title="От 3 до 32 символов: латиница, цифры, подчёркивание"
            required
          />
        </div>

        <PasswordField
          id="password"
          label="Пароль"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={8}
          placeholder="не короче 8 символов"
        />

        <PasswordField
          id="passwordAgain"
          label="Повторите пароль"
          value={passwordAgain}
          onChange={setPasswordAgain}
          autoComplete="new-password"
          minLength={8}
          error={mismatch ? 'Пароли не совпадают' : null}
        />

        <button type="submit" disabled={busy || mismatch}>
          {busy ? 'Создаём…' : 'Присоединиться'}
        </button>

        {error && <p className="error">{error}</p>}
      </form>
    </div>
  );
}
